import { format } from 'date-fns';
import * as React from 'react';
import { PriceString } from '../../../components/PriceString';
import * as Colors from '../../../consts/variables';
import {
    formatItineraryInfo,
    formatUrgencyLabels,
    getRatingLabel,
} from '../../../helpers/cruiseSearchHelper';
import { getTranslatedMonth } from '../../../helpers/date';
import { hasHotelServiceCharges } from '../../../helpers/hotelServiceChargesHelper';
import { replacePlaceholders, __ } from '../../../helpers/TranslationService';
import { getCruiseTitle } from '../../../helpers/cruisePageHelpers';
import { ISearchResultProps } from '../SearchPage';
import { Rating } from './Rating';
import { UrgencyLabel } from './UrgencyLabel';

let style: any = {
    itenararyInfo: {
        fontSize: '12px',
        fontWeight: 700,
        color: '#9b9b9b',
        lineHeight: '22px',
        whiteSpace: 'nowrap',
    },
    ratingAndIntenararyInfoDiv: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        padding: '5px 0px 2px',
    },
    headline: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '22px',
        color: Colors.mineSharf,
        padding: '8px 0px',
    },
    flexSpaceBetweenRow: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
    },
    flexSpaceBetweenColumn: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
    },
    dateRange: {
        fontSize: '13px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    areaTitle: {
        fontSize: '13px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        paddingBottom: '10px',
    },
    numberOfSails: {
        fontSize: '12px',
        lineHeight: '17px',
        color: '#979797',
    },
    companyLogo: {
        height: '60px',
        width: '100px',
        objectFit: 'contain',
        marginTop: 'auto',
    },
    highlightsWrapper: {
        fontSize: '13px',
        fontWeight: 500,
        margin: '0px 15px 0px',
    },
    highlightsList: {
        marginTop: '7px',
    },
    highlightItem: {
        listStyleType: 'disc',
        lineHeight: '18px',
    },
    urgencyLabelWrapper: {
        overflow: 'hidden',
        display: 'grid',
        marginRight: '-8px',
        marginLeft: 'auto',
        maxHeight: '44px',
    },
    priceWrapper: {
        container: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            width: '50%',
            fontSize: 13,
            marginRight: '-8px', //parent padding to look centered relative to labels
            paddingBottom: '12px',
        },
        priceContainer: { display: 'inline-block' },
        priceString: {
            lineHeight: 1.25,
            fontSize: 22,
            fontWeight: 'bold',
            marginRight: 4,
            wordSpacing: '-4px',
        },
        onRequestString: {
            fontSize: '25px',
            lineHeight: '25px',
            fontWeight: 700,
            marginRight: 4,
            wordSpacing: '-4px',
        },
        fromContainer: {},
        fromSpan: {
            display: 'inline-block',
            marginRight: 4,
        },
        includingTextSpan: {
            display: 'inline-block',
        },
        hotelServiceChargesLabel: {
            marginTop: '5px',
            fontSize: '12px',
            fontWeight: 'bold',
        },
        discount: {
            marginTop: '5px',
            fontSize: 12,
            color: Colors.pink,
            fontWeight: 'bold',
        },
        upToDiscountWrapper: {
            width: '33px',
            height: '26px',
            backgroundColor: '#e01a59',
            color: Colors.white,
            fontWeight: 700,
        },
        upToDiscountDiv: {
            display: 'flex',
            margin: '0px 2px 10px 0px',
        },
        upToDiscountString: {
            textAlign: 'center',
            fontSize: '9px',
            marginTop: '2px',
        },
        upToDiscountPercentage: {
            textAlign: 'center',
            lineHeight: '13px',
            fontSize: '11px',
        },
        includingFlightButton: {
            color: '#2486ae',
            backgroundColor: '#d8e9f1',
            fontSize: '12px',
            fontWeight: 'bold',
            padding: '5px 12px',
            marginLeft: 'auto',
            minWidth: '160px',
            textAlign: 'center',
            lineHeight: '1.3',
            width: '166px',
        },
        pricePerPerson: {
            display: 'inlineBlock',
        },

        arrowRight: {
            borderTop: '13px solid transparent',
            borderBottom: '13px solid transparent',
            borderLeft: '6px solid #e01a59',
        },
    },
};

const getCruiseOrPackageBanner = (
    showBanner: boolean,
    includesHotelServiceCharges: boolean,
    isPackageDreamlines: boolean,
    cruisesNotAllowedMarket: boolean,
    isPackageWithExtras: boolean,
) => {
    return (
        <>
            {showBanner ? (
                <div style={style.priceWrapper.includingFlightButton}>
                    {includesHotelServiceCharges
                        ? isPackageDreamlines
                            ? __('package incl. flight and hotel service charges', 'dreamlines')
                            : __('cruise incl. flight and hotel service charges', 'dreamlines')
                        : isPackageDreamlines
                        ? __('package incl. flight', 'dreamlines')
                        : __('cruise incl. flight', 'dreamlines')}
                </div>
            ) : (
                cruisesNotAllowedMarket && (
                    <div style={style.priceWrapper.includingTextSpan}>
                        {__('cruise incl. flight', 'dreamlines')}
                    </div>
                )
            )}
            {isPackageWithExtras ? (
                <div style={style.priceWrapper.includingFlightButton}>
                    {includesHotelServiceCharges
                        ? __('package with extras and hotel service charges', 'dreamlines')
                        : __('package with extras', 'dreamlines')}
                </div>
            ) : null}
        </>
    );
};

const shouldShowDiscount = (props: any): boolean =>
    props.cruise.cheapestSail.price > 0 &&
    props.cruise.cheapestSail.discount > 0 &&
    props.cruise.cheapestSail.discount >= props.discountThreshold;
export class SearchResultItemMiddleSection extends React.Component<ISearchResultProps> {
    render() {
        const { cruise } = this.props;
        const {
            companyTitle,
            nights,
            earliestDeparture,
            latestArrival,
            includesFlight,
            cheapestSail,
            companyLogo,
            shipCompanyLogo,
            cruiseRating,
            cruiseRatingCount,
            shipTitle,
            wayPointCities,
            isPackageCruise,
            urgencyLabel,
            obc,
            companyNid,
        } = cruise;

        const harboursString = cruise?.areaTitles.length
            ? cruise.areaTitle.split(',').join(', ')
            : null;
        const areaTitle = harboursString?.length > 20 ? `${harboursString.slice(0, 20)}...` : null;
        const destination = cruise?.areaTitles.length ? cruise.areaTitles[0] : '';

        const title = getCruiseTitle({
            isDreamlinesPackage: cruise.isPackageCruise,
            companyNid: companyNid,
            shipTitle: shipTitle,
            cruiseTitle: cruise.routeTitle,
            companyTitle: cruise.companyTitle,
            destination: destination,
            startHarbour: cruise.startHarbourName,
        });

        const priceProps = {
            currency: cheapestSail.currency,
            price: cheapestSail.price,
        };
        const uniqueWayPointCities = Array.from(new Set(wayPointCities));
        const nightsPortsDaysAtSea = formatItineraryInfo(nights, uniqueWayPointCities.length);

        const formattedUrgencyLabels = formatUrgencyLabels(urgencyLabel, obc, isPackageCruise);
        const formattedEarliestDeparture = new Date(earliestDeparture * 1000);
        const formattedLatestArrival = new Date(latestArrival * 1000);
        const getYearFormatter = (singleSail: boolean) => {
            return format(formattedEarliestDeparture, 'yyyy') ===
                format(formattedLatestArrival, 'yyyy')
                ? ''
                : singleSail
                ? '.yyyy'
                : 'yyyy';
        };

        const dateRange =
            cruise.sails.length === 1
                ? `${format(
                      formattedEarliestDeparture,
                      'dd.MM' + getYearFormatter(true),
                  )} - ${format(formattedLatestArrival, 'dd.MM.yyyy')}`
                : `${getTranslatedMonth(formattedEarliestDeparture.getMonth())} ${
                      getYearFormatter(false)
                          ? format(formattedEarliestDeparture, getYearFormatter(false))
                          : ''
                  } - ${getTranslatedMonth(formattedLatestArrival.getMonth())} ${format(
                      formattedLatestArrival,
                      'yyyy',
                  )}`;
        // Yes is unreliable
        const formattedNumberOfSails =
            'Даты' === __('Dates', 'dreamlines')
                ? `${__('Dates', 'dreamlines')}: ${cruise.sails.length}`
                : `${cruise.sails.length} ${__('Dates', 'dreamlines')}`;

        const includesHotelServiceCharges = hasHotelServiceCharges(
            this.props.portalId,
            cruise.companyTitle,
            cruise.shipTitle,
            cheapestSail.departure,
        );

        const allowedMarketFlags = this.props.portalId.indexOf('dreamlines.de') > -1;

        const isPackageDreamlines = cruise.isPackageCruise;

        const priceContainerWidth = {
            width:
                allowedMarketFlags && (includesFlight || isPackageDreamlines)
                    ? 'fit-content'
                    : '50%',
        };

        const isFlightOrPackage = includesFlight || isPackageDreamlines;

        const showBanner = allowedMarketFlags && includesFlight;

        const cruisesNotAllowedMarket = !allowedMarketFlags && includesFlight;

        const isPackageWithExtras = allowedMarketFlags && !includesFlight && isPackageDreamlines;

        const hotelChargesAsPlainText =
            includesHotelServiceCharges &&
            ((allowedMarketFlags && !isFlightOrPackage) || !allowedMarketFlags);

        return (
            <>
                <div style={style.ratingAndIntenararyInfoDiv}>
                    <div style={style.itenararyInfo}>{nightsPortsDaysAtSea}</div>
                    {cruiseRating > 0 && (
                        <Rating
                            value={cruiseRating * 2}
                            string={getRatingLabel(cruiseRating * 2)}
                            amount={cruiseRatingCount}
                        />
                    )}
                </div>
                <span style={style.headline}> {title}</span>
                <div style={style.flexSpaceBetweenColumn}>
                    <div style={style.flexSpaceBetweenRow}>
                        <div style={style.flexSpaceBetweenColumn}>
                            {areaTitle && (
                                <span className="icon-globe" style={style.areaTitle}>
                                    {' '}
                                    {areaTitle}
                                </span>
                            )}
                            <span style={style.dateRange}>{dateRange}</span>
                            {cruise.sails.length > 1 && (
                                <span style={style.numberOfSails}>{formattedNumberOfSails}</span>
                            )}
                        </div>

                        <div style={style.urgencyLabelWrapper}>
                            {formattedUrgencyLabels.length > 0 &&
                                formattedUrgencyLabels.map((urgencyLabel) => {
                                    return (
                                        <UrgencyLabel
                                            key={urgencyLabel.type}
                                            label={urgencyLabel}
                                            isNewDesign={true}
                                        />
                                    );
                                })}
                        </div>
                    </div>
                    <div style={style.flexSpaceBetweenRow}>
                        <img
                            style={{
                                ...style.companyLogo,
                            }}
                            alt={companyTitle}
                            src={isPackageCruise ? shipCompanyLogo : companyLogo}
                        />

                        <div style={{ ...style.priceWrapper.container, ...priceContainerWidth }}>
                            {shouldShowDiscount(this.props) && (
                                <span style={style.priceWrapper.discount}>
                                    {replacePlaceholders(__('upToXDiscount', 'dreamlines'), [
                                        `-${cheapestSail.discount}%`,
                                    ])}
                                </span>
                            )}
                            {cheapestSail.price > 0 ? (
                                <>
                                    <div>
                                        <span style={style.priceWrapper.fromSpan}>
                                            {__('from', 'dreamlines')}
                                        </span>
                                        <div style={style.priceWrapper.priceContainer}>
                                            <span style={style.priceWrapper.priceString}>
                                                <PriceString {...priceProps} />
                                            </span>
                                            <span style={style.priceWrapper.pricePerPerson}>
                                                {__('p.P.', 'dreamlines')}
                                            </span>
                                        </div>
                                    </div>
                                    {getCruiseOrPackageBanner(
                                        showBanner,
                                        includesHotelServiceCharges,
                                        isPackageDreamlines,
                                        cruisesNotAllowedMarket,
                                        isPackageWithExtras,
                                    )}

                                    {hotelChargesAsPlainText && (
                                        <div style={style.priceWrapper.hotelServiceChargesLabel}>
                                            {__('incl. hotel service charges', 'dreamlines')}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div style={style.priceWrapper.priceString}>
                                    {__('on request', 'dreamlines')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}
